import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const boutiques = [
  {
    key: 'martigny',
    name: 'Martigny',
    address: 'Rue des Petits-Epineys 3',
    city: '1920 Martigny',
    phone: '027 722 27 50',
    phoneLink: '+41277222750',
    email: 'info@lesnocesdeginkgo.ch',
    mapsUrl: 'https://maps.google.com/?q=Rue+des+Petits-Epineys+3+1920+Martigny',
    mapsEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2742.7!2d7.079!3d46.102!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ec92af2c45fed%3A0x1!2sRue+des+Petits-Epineys+3%2C+1920+Martigny!5e0!3m2!1sfr!2sch!4v1',
    hours: [
      { dayKey: 'mon_fri', hoursKey: 'martigny_hours_weekday' },
      { dayKey: 'saturday', hoursKey: 'martigny_hours_sat' },
      { dayKey: 'sunday', hoursKey: 'martigny_hours_sun' },
    ],
    color: 'bg-ginkgo-800',
  },
  {
    key: 'verbier',
    name: 'Verbier',
    address: 'Rte des Creux 6',
    city: '1936 Verbier',
    phone: '027 565 57 50',
    phoneLink: '+41275655750',
    email: 'info@lesnocesdeginkgo.ch',
    mapsUrl: 'https://maps.google.com/?q=Rte+des+Creux+6+1936+Verbier',
    hours: [
      { dayKey: 'winter_season', hoursKey: null },
      { dayKey: 'mon_fri', hoursKey: 'verbier_hours' },
      { dayKey: 'sunday', hoursKey: 'verbier_hours_sun' },
    ],
    color: 'bg-ginkgo-700',
  },
  {
    key: 'sion',
    name: 'Sion',
    address: 'Av. du Ritz 33',
    city: '1950 Sion',
    phone: '027 323 23 16',
    phoneLink: '+41273232316',
    email: 'sion@lesnocesdeginkgo.ch',
    mapsUrl: 'https://maps.google.com/?q=Av+du+Ritz+33+1950+Sion',
    hours: [
      { dayKey: 'mon_fri', hoursKey: 'sion_hours_weekday' },
      { dayKey: 'saturday', hoursKey: 'sion_hours_sat' },
      { dayKey: 'sunday', hoursKey: 'sion_hours_sun' },
    ],
    color: 'bg-ginkgo-600',
  },
];

const Contact = () => {
  const { t } = useTranslation();

  useEffect(() => { document.title = `${t('contact.title')} — Les Noces de Ginkgo`; }, [t]);

  return (
    <div className="font-sans bg-cream min-h-screen pt-16">

      {/* Header */}
      <section className="bg-ginkgo-900 py-20 md:py-28 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-medium mb-4">Valais, Suisse</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white font-medium">{t('contact.title')}</h1>
          <p className="mt-3 text-white/60 text-base">{t('contact.subtitle')}</p>
        </motion.div>
      </section>

      {/* Boutiques grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {boutiques.map((b, i) => (
              <motion.div
                key={b.key}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="bg-white border border-stone-100"
                data-testid={`contact-boutique-${b.key}`}
              >
                {/* Header band */}
                <div className={`${b.color} px-6 py-5`}>
                  <h2 className="font-serif text-3xl text-white">{b.name}</h2>
                </div>

                <div className="p-6 space-y-6">
                  {/* Address */}
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-ginkgo-700 font-medium mb-3 flex items-center gap-2">
                      <MapPin size={12} /> {t('contact.address')}
                    </h3>
                    <a
                      href={b.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-maps-${b.key}`}
                      className="flex items-start gap-2 text-stone-600 text-sm hover:text-ginkgo-800 transition-colors group"
                    >
                      <span className="leading-relaxed">{b.address}<br />{b.city}</span>
                      <ExternalLink size={12} className="mt-0.5 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />
                    </a>
                  </div>

                  {/* Hours */}
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-ginkgo-700 font-medium mb-3 flex items-center gap-2">
                      <Clock size={12} /> {t('contact.hours')}
                    </h3>
                    <div className="space-y-2">
                      {b.hours.map(({ dayKey, hoursKey }) => (
                        <div key={dayKey} className="flex justify-between text-sm">
                          <span className="text-stone-500">
                            {dayKey === 'winter_season'
                              ? <em className="text-gold text-xs not-italic font-medium uppercase tracking-wide">{t(`contact.${dayKey}`)}</em>
                              : t(`contact.${dayKey}`)
                            }
                          </span>
                          {hoursKey && (
                            <span className={`text-stone-800 font-medium text-xs ${t(`contact.${hoursKey}`) === t('contact.closed') ? 'text-stone-400 italic' : ''}`}>
                              {t(`contact.${hoursKey}`)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-ginkgo-700 font-medium mb-3 flex items-center gap-2">
                      <Phone size={12} /> {t('contact.phone')}
                    </h3>
                    <a
                      href={`tel:${b.phoneLink}`}
                      data-testid={`contact-phone-${b.key}`}
                      className="text-sm text-stone-700 hover:text-ginkgo-800 transition-colors font-medium"
                    >
                      {b.phone}
                    </a>
                  </div>

                  {/* Email */}
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-ginkgo-700 font-medium mb-3 flex items-center gap-2">
                      <Mail size={12} /> {t('contact.email')}
                    </h3>
                    <a
                      href={`mailto:${b.email}`}
                      data-testid={`contact-email-${b.key}`}
                      className="text-sm text-stone-700 hover:text-ginkgo-800 transition-colors break-all"
                    >
                      {b.email}
                    </a>
                  </div>

                  {/* Get directions */}
                  <a
                    href={b.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-directions-${b.key}`}
                    className="flex items-center gap-2 text-xs uppercase tracking-widest text-ginkgo-800 font-medium hover:text-gold transition-colors"
                  >
                    <MapPin size={12} />
                    {t('contact.get_directions')}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map section */}
      <section className="bg-ginkgo-50 py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="font-serif text-3xl text-ginkgo-800">Nous retrouver</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {boutiques.map((b) => (
              <motion.div key={b.key} {...fadeUp} className="bg-white border border-stone-100 overflow-hidden">
                <a href={b.mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="bg-ginkgo-100 h-48 flex items-center justify-center relative overflow-hidden group">
                    <div className="text-center">
                      <MapPin size={32} className="text-ginkgo-700 mx-auto mb-2" />
                      <p className="font-serif text-xl text-ginkgo-800">{b.name}</p>
                      <p className="text-sm text-stone-500 mt-1">{b.address}, {b.city}</p>
                    </div>
                    <div className="absolute inset-0 bg-ginkgo-800/5 group-hover:bg-ginkgo-800/10 transition-colors" />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-stone-600">{b.address}, {b.city}</span>
                    <ExternalLink size={14} className="text-ginkgo-600" />
                  </div>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
